import React from "react";
import { splitFormControlWrapperProps } from "@cmp-src/form/FormControlWrapper/helpers";
import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "@cmp-src/form/FormControlWrapper/FormControlWrapperProvider";
import {
  Category,
  CategorySelectProps,
  CategorySelectPropsWithFormControl
} from "./types";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { Button } from "@cmp-src/primitives/button";
import { TextInput } from "@cmp-src/form/TextInput";
import {
  addParentReferences,
  findCategoryPath,
  flattenCategories
} from "./helpers";
import Fuse from "fuse.js";
import { CategorySelectList } from "@cmp-src/form/CategorySelect/CategorySelectList";
import { SearchIcon } from "@cmp-src/icons/icons";
import { useDisclosure } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { Slot } from "@radix-ui/react-slot";
import { XIcon } from "lucide-react";

/**
 * The bare picker of `CategorySelect`, without label or error. A custom `trigger` must be a
 * single button element.
 */
export const CategorySelectInput = React.forwardRef<
  HTMLInputElement,
  CategorySelectProps
>((props, ref) => {
  const { categories, trigger, onSelect, value } = props;

  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");
  // Inside CategorySelect the field label points at the trigger by id
  const { id } = useFormControlWrapper();
  // A button takes no aria-required: only point it at the helper text and error
  const { "aria-describedby": ariaDescribedBy } = useFormControlInputA11y();

  const popoverDisclosure = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [internalSelectedCategory, setInternalSelectedCategory] =
    React.useState<Category | null>(null);
  const [navigationStack, setNavigationStack] = React.useState<Category[]>([]);

  // Determine if the component is controlled
  const isControlled = value !== undefined && onSelect !== undefined;
  const selectedCategory = isControlled ? value : internalSelectedCategory;

  // Memoize the categoriesWithParents to prevent unnecessary recalculations
  const categoriesWithParents = React.useMemo(() => {
    return addParentReferences(categories);
  }, [categories]);

  // Memoize the Fuse instance and flattened categories
  const { fuse } = React.useMemo(() => {
    const allCats = flattenCategories(categoriesWithParents);
    const fuseInstance = new Fuse(allCats, {
      keys: ["name", "keywords"],
      threshold: 0.3,
      distance: 100
    });
    return { fuse: fuseInstance, allCategories: allCats };
  }, [categoriesWithParents]);

  // Memoize the search results
  const currentCategories = React.useMemo(() => {
    if (searchTerm) {
      const results = fuse.search(searchTerm);

      return results.map((result) => result.item);
    }
    const currentParent =
      navigationStack[navigationStack.length - 1] || categoriesWithParents;
    return currentParent.children || [];
  }, [navigationStack, searchTerm, fuse, categoriesWithParents]);

  React.useEffect(() => {
    if (
      popoverDisclosure.isOpen &&
      selectedCategory &&
      navigationStack.length === 0
    ) {
      setNavigationStack(
        findCategoryPath(selectedCategory.id, categoriesWithParents)
      );
    }
  }, [popoverDisclosure.isOpen, selectedCategory, categoriesWithParents]);

  const handleSelect = (category: Category) => {
    if (category.children) {
      if (searchTerm) {
        const path = findCategoryPath(category.id, categoriesWithParents);
        setNavigationStack([...path, category]);
        setSearchTerm("");
      } else {
        setNavigationStack([...navigationStack, category]);
      }
    } else {
      if (!isControlled) {
        setInternalSelectedCategory(category);
      }
      onSelect?.(category);
      popoverDisclosure.close();
    }
  };

  const handleBack = () => {
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const handleOpenChange = (open: boolean) => {
    popoverDisclosure.onOpenChange(open);
    if (!open) {
      setTimeout(() => {
        setNavigationStack([]);
        setSearchTerm("");
      }, 200);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // A single button element: it becomes the trigger itself (asChild / Slot)
  const triggerContent = trigger || (
    <Button
      variant="trigger"
      className={"w-full px-2 shadow-lg md:shadow-none"}
      size={"trigger"}
      id={id}
      aria-describedby={ariaDescribedBy}
    >
      <span className={"block max-w-full overflow-hidden truncate"}>
        {selectedCategory?.name || "Select Category"}
      </span>
    </Button>
  );

  const content = (
    <>
      <div className={"w-full"}>
        <div className="relative border-b-1 border-b-neutral-100 p-0.5">
          <TextInput
            placeholder="Search"
            aria-label="Search categories"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={"w-full"}
            wrapperClassName={"border-0 shadow-none px-4"}
            inputClassName={"h-5 py-3 outline-none"}
            leftIcon={<SearchIcon className={"size-4"} />}
          />
          {searchTerm && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={handleClearSearch}
              className="absolute top-2.5 right-2 text-content hover:text-foreground"
            >
              <XIcon className="size-4" />
            </button>
          )}
        </div>
        <ScrollArea className="h-[250px] w-full">
          <CategorySelectList
            categories={currentCategories}
            onSelect={handleSelect}
            onBack={navigationStack.length > 0 ? handleBack : undefined}
            navigationStack={navigationStack}
            showPath={!!searchTerm}
            allCategories={categoriesWithParents}
          />
        </ScrollArea>
      </div>
    </>
  );

  if (!isMd) {
    return (
      <>
        <Slot onClick={() => popoverDisclosure.toggle()}>
          {triggerContent}
        </Slot>
        <MobileDrawer
          isOpen={popoverDisclosure.isOpen}
          onOpenChange={handleOpenChange}
          ariaLabel="Categories"
        >
          {content}
        </MobileDrawer>
      </>
    );
  }

  return (
    <Popover
      className={"w-[252px]"}
      bodyProps={{ className: "p-0 w-full" }}
      contentProps={{ "aria-label": "Categories" }}
      content={content}
      open={popoverDisclosure.isOpen}
      onOpenChange={handleOpenChange}
      triggerAsChild
    >
      {triggerContent}
    </Popover>
  );
});

CategorySelectInput.displayName = "CategorySelectInput";

/**
 * A labelled picker for one leaf of a category tree, browsed one level at a time or found by
 * searching names and `keywords` across all levels. Opens a popover on desktop and a drawer
 * below `md`. Controlled with `value` + `onSelect`; without `value` it keeps the choice itself.
 */
export const CategorySelect = React.forwardRef<
  HTMLInputElement,
  CategorySelectPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper variant={"unstyled"} {...wrapperProps}>
        <CategorySelectInput ref={ref} {...inputProps} />
      </FormControlWrapper>
    </>
  );
});

CategorySelect.displayName = "CategorySelect";
